import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { importItemsActions } from '@common-reducers/ImportItemsReducer'
import BtEditableBox from '@common-components/tasks/bt-editable-box/bt-editable-box'
import { TaskColumnType } from '@common-components/tasks/columns-cells/cell-interface'
import { columnTypeList } from '@common-components/tasks/columns-cells/column-cell-defs'
import AddColumnDialog from '@common-components/tasks/picker-dialog/pickers/add-column-dialog'
import { getIconByType } from '@common/components/tasks/columns-cells/column-cell-defs'
import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'


type Props = {
    supportedColumns?: Array<{ _id?: string; type?: string; title?: string }>,
    open: boolean,
    onClose: () => void,
}


export default function ManageColumnsForImportExcel({
    supportedColumns,
    open,
    onClose,
}: Props) {
    const dispatch = useAppDispatch()

    const newColumnList = useAppSelector((state) => state.ImportItemsReducer.newColumnList);
    const importDataMapping = useAppSelector((state) => state.ImportItemsReducer.importDataMapping);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);

    const inputTheme = {
        cursor: 'text',
        maxWidth: "200px",
        color: "var(--color-primary)",
        fontSize: '13px',
        border: '1px solid var(--color-accent)',
        boxSizing: 'border-box',
        borderRadius: '4px',
        fontFamily: "'Inter', sans-serif",
        fontWeight: '600',
        height: '24px',
        "& .MuiInputBase-input": {
            padding: "2px 6px",
            borderRadius: '4px',
        }
    }
    const textTheme = {
        cursor: "text",
        maxWidth: "200px",
        color: "var(--tasks-text-muted)",
        whiteSpace: "nowrap",
        textOverflow: 'ellipsis',
        outline: "none",
        backgroundColor: "transparent",
        fontSize: '13px',
        border: '1px solid transparent',
        borderRadius: '4px',
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
        padding: "4px",
        lineHeight: '16px',
        "&:hover": {
            border: "1px dashed var(--tasks-text-secondary);",
        }
    }

    const handleClosePopover = () => {
        setAddPopoverOpen(false);
    };

    const getColumnTitleByType = (type: string) => {
        return columnTypeList.find(columnType => columnType.type === type)?.title
    }

    const createNewColumnId = () => {
        const prefix = 'NEWCOLUMN ';
        const randomString = Math.random().toString(36).substring(2, 16) + Math.random().toString(36).substring(2, 10);
        const result = prefix + randomString.substring(0, 14);
        return result;
    }

    const addColumn = (columnDescriptor: TaskColumnType<any>) => {
        dispatch(importItemsActions.setNewColumnList([...newColumnList, { _id: createNewColumnId(), type: columnDescriptor.type, title: `New ${columnDescriptor.title}` }]))
        handleClosePopover()
    }


    const handleRemoveNewColumn = (newColumn: { _id?: string }) => {
        dispatch(importItemsActions.setNewColumnList(newColumnList.filter(column => column !== newColumn)))

        dispatch(importItemsActions.setImportDataMapping({
            ...Object.fromEntries(Object.entries(importDataMapping).filter(([key, value]) => value !== newColumn._id))
        }))
    }


    return (
        <>
            <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
                <DialogContent className="max-w-md max-h-[600px] flex flex-col p-0">
                    <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-700">
                        <DialogTitle className="text-xl font-bold">Manage Columns</DialogTitle>
                        <DialogDescription className="text-sm text-slate-600 dark:text-slate-400">
                            Add, edit, or remove columns for import mapping
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="flex-1 px-6 py-4">
                        <div className="space-y-3">
                            {/* Existing Columns */}
                            <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                    Existing Columns
                                </h4>
                                {supportedColumns?.map((column, i) => (
                                    <Card key={i} className="p-3 border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                                        <div className="flex items-center gap-2">
                                            <div className="flex-shrink-0">
                                                {getIconByType(column?.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm font-medium text-[var(--color-text)] truncate">
                                                    {column?.title}
                                                </span>
                                            </div>
                                            <Badge variant="secondary" className="text-xs">
                                                {getColumnTitleByType(column?.type ?? '')}
                                            </Badge>
                                        </div>
                                    </Card>
                                ))}
                            </div>

                            {/* New Columns */}
                            {newColumnList.length > 0 && (
                                <div className="space-y-2 pt-2">
                                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                        New Columns
                                    </h4>
                                    {newColumnList.map((newColumn, i) => (
                                        <Card key={i} className="p-3 border-slate-200 dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/30 hover:bg-blue-100/50 dark:hover:bg-blue-950/50 transition-colors">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-shrink-0">
                                                    {getIconByType(newColumn?.type)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <BtEditableBox
                                                        text={newColumn?.title}
                                                        inputSx={inputTheme}
                                                        boxSx={textTheme}
                                                        shouldAutoFlex={false}
                                                        placeholder={newColumn.title}
                                                        onChange={(value) => {
                                                            dispatch(importItemsActions.setNewColumnList(newColumnList.map(column => column === newColumn ? { ...column, title: value } : column)))
                                                        }} />
                                                </div>
                                                <Badge variant="secondary" className="text-xs">
                                                    {getColumnTitleByType(newColumn?.type)}
                                                </Badge>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-950"
                                                    onClick={() => handleRemoveNewColumn(newColumn)}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </ScrollArea>

                    <DialogFooter className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex-row justify-between gap-2">
                        <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="gap-2">
                                    <Plus className="h-4 w-4" />
                                    Add Column
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent 
                                align="start" 
                                className="w-64 max-h-[400px] overflow-y-auto p-0"
                                zIndex={30000}
                                onInteractOutside={(e) => {
                                    // Prevent dialog from intercepting clicks
                                    e.preventDefault();
                                }}
                            >
                                <AddColumnDialog addNewColumnToImportModule={addColumn} />
                            </PopoverContent>
                        </Popover>

                        <Button onClick={onClose} className="bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90">
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
